import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import type { AuthResponse, ProblemDetails } from './types';

const STORAGE_KEY = 'stayfinder.session';

export interface StoredSession {
  accessToken: string;
  refreshToken: string;
  accessTokenExpiresAtUtc: string;
}

export const sessionStore = {
  read(): StoredSession | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as StoredSession) : null;
    } catch {
      // A private window or blocked storage must not break the app.
      return null;
    }
  },
  write(session: StoredSession) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    } catch {
      /* ignore */
    }
  },
  clear() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
  },
};

export const api: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '',
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
});

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const session = sessionStore.read();

  if (session?.accessToken) {
    config.headers.Authorization = `Bearer ${session.accessToken}`;
  }

  return config;
});

// A single in-flight refresh, shared by every request that hits a 401 at the same moment.
// Without this, a page that fires several requests at once would rotate the refresh token
// concurrently - and the server treats a replayed refresh token as theft.
let refreshInFlight: Promise<string | null> | null = null;

let onSessionExpired: (() => void) | null = null;

export function setSessionExpiredHandler(handler: () => void) {
  onSessionExpired = handler;
}

async function refreshAccessToken(): Promise<string | null> {
  const session = sessionStore.read();

  if (!session?.refreshToken) {
    return null;
  }

  try {
    const { data } = await axios.post<AuthResponse>(
      `${import.meta.env.VITE_API_BASE_URL || ''}/api/auth/refresh`,
      { refreshToken: session.refreshToken },
      { headers: { 'Content-Type': 'application/json' } },
    );

    sessionStore.write({
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
      accessTokenExpiresAtUtc: data.accessTokenExpiresAtUtc,
    });

    return data.accessToken;
  } catch {
    sessionStore.clear();
    return null;
  }
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as InternalAxiosRequestConfig & { _retried?: boolean };

    const isAuthEndpoint = original?.url?.includes('/api/auth/');

    if (error.response?.status === 401 && original && !original._retried && !isAuthEndpoint) {
      original._retried = true;

      refreshInFlight ??= refreshAccessToken().finally(() => {
        refreshInFlight = null;
      });

      const token = await refreshInFlight;

      if (token) {
        original.headers.Authorization = `Bearer ${token}`;
        return api(original);
      }

      onSessionExpired?.();
    }

    return Promise.reject(error);
  },
);

/**
 * What kind of failure this was.
 *
 * The distinction matters on screen: a request that never reached the server deserves a
 * retry button, a 403 does not, and neither should be rendered as "no results". The
 * message alone cannot carry that, so callers branch on this instead of matching strings.
 */
export type ApiErrorKind =
  | 'network'
  | 'timeout'
  | 'validation'
  | 'auth'
  | 'forbidden'
  | 'notFound'
  | 'conflict'
  | 'server'
  | 'unknown';

/** A readable message for the user, plus any field-level errors for the form. */
export interface ApiError {
  message: string;
  status?: number;
  kind: ApiErrorKind;
  /** Whether trying the same request again could plausibly succeed. */
  retryable: boolean;
  fieldErrors: Record<string, string[]>;
}

/** Maps an HTTP status to the kind of failure it represents. */
function kindForStatus(status: number | undefined): ApiErrorKind {
  if (status === undefined) return 'unknown';
  if (status === 401) return 'auth';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'notFound';
  if (status === 409) return 'conflict';
  if (status === 422 || status === 400) return 'validation';
  if (status >= 500) return 'server';
  return 'unknown';
}

/** A server error may pass; a 403 never will, however many times it is tried. */
const RETRYABLE: ReadonlySet<ApiErrorKind> = new Set(['network', 'timeout', 'server']);

export function toApiError(error: unknown): ApiError {
  if (axios.isAxiosError(error)) {
    const problem = error.response?.data as ProblemDetails | undefined;

    if (problem?.errors && Object.keys(problem.errors).length > 0) {
      const first = Object.values(problem.errors)[0]?.[0];
      return {
        message: first ?? problem.title ?? 'Please check the highlighted fields.',
        status: error.response?.status,
        kind: 'validation',
        retryable: false,
        // Keys arrive PascalCased from model validation; lower-case them for form lookup.
        fieldErrors: Object.fromEntries(
          Object.entries(problem.errors).map(([key, value]) => [lowerFirst(key), value]),
        ),
      };
    }

    if (problem?.detail) {
      const kind = kindForStatus(error.response?.status);
      return {
        message: problem.detail,
        status: error.response?.status,
        kind,
        retryable: RETRYABLE.has(kind),
        fieldErrors: {},
      };
    }

    if (error.code === 'ECONNABORTED') {
      return {
        message: 'The request timed out. Check your connection and try again.',
        kind: 'timeout',
        retryable: true,
        fieldErrors: {},
      };
    }

    if (!error.response) {
      return {
        message: 'We could not load this information. Check your connection and try again.',
        kind: 'network',
        retryable: true,
        fieldErrors: {},
      };
    }

    const kind = kindForStatus(error.response.status);
    return {
      message: problem?.title ?? 'Something went wrong. Please try again.',
      status: error.response.status,
      kind,
      retryable: RETRYABLE.has(kind),
      fieldErrors: {},
    };
  }

  return {
    message: 'Something went wrong. Please try again.',
    kind: 'unknown',
    retryable: false,
    fieldErrors: {},
  };
}

function lowerFirst(value: string): string {
  // "PgDetail.CurfewTime" -> "pgDetail.CurfewTime"
  return value.length > 0 ? value[0].toLowerCase() + value.slice(1) : value;
}

/** Builds a query string, dropping empty values and expanding arrays into repeats. */
export function toQuery(params: Record<string, unknown>): string {
  const search = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;

    if (Array.isArray(value)) {
      value.filter((v) => v !== undefined && v !== null && v !== '').forEach((v) => search.append(key, String(v)));
    } else {
      search.append(key, String(value));
    }
  }

  const query = search.toString();
  return query ? `?${query}` : '';
}
